import React from 'react'
import { RouteComponentProps } from 'react-router-dom'
import {
  AllUsers_users_users,
  getFormattedAddress,
  useStateWithDebounce,
  useCompanyDetails,
  useCompanyDetailsTestSessionPagination,
  useInviteCompanyMember,
  CompanyMemberRole,
  useRemoveCompanyMember,
  useUsers,
  ALL_USERS,
} from '@modmd/data'
import { FindOrInviteUser } from 'sharedComponents/FindOrInviteUser'
import { DEVICE, COLOR } from 'theme'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import { CompanyPageLayout } from 'client-portal/components/PageLayout'
import Pagination from 'components/Pagination'
import Card from 'components/Card'
import Button from 'components/Button'
import TextInput from 'components/TextInput'
import Icon from 'components/Icon'
import Datalist from 'components/Datalist'
import { Details } from 'sharedComponents/Details'
import { Dialog } from 'components/Dialog'
import { Box } from 'components/Layout'
import { useToaster } from 'utils/useToaster'
import { ROUTES, COMPANY_ROUTES } from 'client-portal/constants/routes'
import { Spinner } from 'components/Spinner'

interface RouterProps {
  companyId: string
}

type Props = RouteComponentProps<RouterProps>

const SKELETON_DETAILS_COLUMNS = 4
const SKELETON_DETAILS_ROWS = 1

const SKELETON_MEMBERS_COLUMNS = 4
const PAGE_LENGTH = 5

export const CompanyDetails: React.VFC<Props> = ({ match, history }) => {
  const [page, setPage] = React.useState(1)
  const [rowId, setRowId] = React.useState<string | null>(null)
  const [emailOrNameLike, setEmailOrNameLike, debouncedEmailOrNameLike] = useStateWithDebounce('')
  const { params } = match
  const { setToastMessage } = useToaster()
  const [isDialogOpen, setIsDialogOpen] = React.useState(false)
  const isDesktop = useIsMinDevice(DEVICE.DESKTOP)
  const { data: companyDetailsData, loading: isCompanyDetailsLoading } = useCompanyDetails({
    filter: { companyId: params.companyId, skipGroups: true },
  })
  const {
    data: sessionsData,
    loading: isSessionDataLoading,
  } = useCompanyDetailsTestSessionPagination({
    companyId: params.companyId,
  })
  const { getUsers, users } = useUsers()

  const refetchQueryOptions = React.useMemo(
    () => ({
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: ALL_USERS,
          variables: {
            filterData: {
              isDeleted: false,
              Company: {
                memberRole: CompanyMemberRole.Manager,
                id: params.companyId,
              },
            },
            pagination: {
              from: (page - 1) * PAGE_LENGTH,
              length: PAGE_LENGTH,
            },
            verification: {
              companyIds: [params.companyId],
            },
          },
        },
      ],
    }),
    [page, params.companyId]
  )

  const [inviteCompanyMember, { loading: isInviteCompanyMemberLoading }] = useInviteCompanyMember({
    options: refetchQueryOptions,
  })

  const { removeCompanyMember, isRemoveCompanyMemberLoading } = useRemoveCompanyMember({
    options: refetchQueryOptions,
  })

  React.useEffect(() => {
    getUsers({
      variables: {
        filterData: {
          isDeleted: false,
          Company: {
            memberRole: CompanyMemberRole.Manager,
            id: params.companyId,
          },
        },
        pagination: {
          from: (page - 1) * PAGE_LENGTH,
          length: PAGE_LENGTH,
        },
        verification: {
          companyIds: [params.companyId],
        },
      },
    })
  }, [getUsers, params.companyId, page])

  const fullCompanyDetails =
    companyDetailsData?.company && sessionsData?.testSessions
      ? {
          ...companyDetailsData.company,
          numberOfSessions: sessionsData.testSessions.pagination.total,
        }
      : null

  const handleRemoveCompanyManager = React.useCallback(
    async ({ id, name }: { id: string; name: string }) => {
      if (window.confirm(`Are you sure you want to delete user ${name}?`)) {
        const { companyId } = params
        try {
          setRowId(id)
          await removeCompanyMember({
            variables: {
              inputData: {
                companyId,
                userId: id,
              },
            },
          })
          setToastMessage('Member successfully deleted', 'success')
        } catch {
          // ignore
        }
        setRowId(null)
      }
    },
    [params, removeCompanyMember, setToastMessage]
  )

  const handleAddCompanyManager = React.useCallback(
    async (user: Omit<AllUsers_users_users, '__typename'>) => {
      const { companyId } = params
      try {
        await inviteCompanyMember({
          variables: {
            inputData: {
              companyId,
              role: CompanyMemberRole.Manager,
              userId: user.id,
            },
          },
        })
        setToastMessage('Member successfully added', 'success')
        setIsDialogOpen(false)
      } catch {
        // ignore
      }
    },
    [inviteCompanyMember, params, setToastMessage]
  )

  React.useEffect(() => {
    setPage(1)
    getUsers({
      variables: {
        filterData: {
          isDeleted: false,
          emailOrNameLike: debouncedEmailOrNameLike,
          Company: {
            memberRole: CompanyMemberRole.Manager,
            id: params.companyId,
          },
        },
        pagination: {
          from: (page - 1) * PAGE_LENGTH,
          length: PAGE_LENGTH,
        },
        verification: {
          companyIds: [params.companyId],
        },
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedEmailOrNameLike])

  const userCount = users.data?.users.pagination.total ?? 0
  const pageLength = users.data?.users.pagination.length ?? 0

  return (
    <CompanyPageLayout>
      <Details
        topContent={
          <Datalist
            isLoading={isCompanyDetailsLoading || isSessionDataLoading}
            skeletonProps={{
              columns: SKELETON_DETAILS_COLUMNS,
              rows: SKELETON_DETAILS_ROWS,
            }}
            header={<Datalist.Title>Company details</Datalist.Title>}
            data={fullCompanyDetails ? [fullCompanyDetails] : []}
            renderTableHeader={() => (
              <Datalist.Row display="grid" gridTemplateColumns="repeat(4, 1fr)">
                <Datalist.HeaderCell label="Compnay name" />
                <Datalist.HeaderCell label="Number of people" />
                <Datalist.HeaderCell label="Company location" />
                <Datalist.HeaderCell label="Number of sessions" />
              </Datalist.Row>
            )}
            shouldRenderTableHeader={isDesktop}
            renderTableRow={({ name, Members, Address, numberOfSessions }) => (
              <Datalist.Row
                display="grid"
                gridTemplateColumns={{
                  _: 'repeat(2, 1fr)',
                  DESKTOP: 'repeat(4, 1fr)',
                }}
              >
                <Datalist.Cell label="Company name">{name}</Datalist.Cell>
                <Datalist.Cell label="Number of people">{Members.pagination.total}</Datalist.Cell>
                <Datalist.Cell label="Company location">
                  {getFormattedAddress(Address)}
                </Datalist.Cell>
                <Datalist.Cell label="Number of sessions">{numberOfSessions}</Datalist.Cell>
              </Datalist.Row>
            )}
          />
        }
      >
        <Card width="100%">
          <Datalist
            isLoading={users.loading}
            skeletonProps={{
              columns: SKELETON_MEMBERS_COLUMNS,
              rows: PAGE_LENGTH,
            }}
            header={
              <Datalist.Title
                rightContent={
                  <>
                    <TextInput
                      leftIcon={<Icon.Search />}
                      placeholder="Search"
                      value={emailOrNameLike}
                      onChange={(event) => setEmailOrNameLike(event.target.value)}
                    />
                    <Button onClick={() => setIsDialogOpen(true)}>Invite new manager</Button>
                  </>
                }
              >
                Company managers
              </Datalist.Title>
            }
            data={
              users.data?.users.users.map(({ id, firstName, lastName, email }) => ({
                id,
                name: `${(firstName as string) || ''} ${(lastName as string) || ''}`,
                email,
              })) || []
            }
            renderTableHeader={() => (
              <Datalist.Row display="grid" gridTemplateColumns="repeat(4, 1fr)">
                <Datalist.HeaderCell label="id" />
                <Datalist.HeaderCell label="name" />
                <Datalist.HeaderCell label="Email" />
              </Datalist.Row>
            )}
            shouldRenderTableHeader={isDesktop}
            renderTableRow={({ id, name, email }) => (
              <Datalist.Row
                onClick={() =>
                  history.push(
                    `/${ROUTES.COMPANY}/${params.companyId}/${COMPANY_ROUTES.MEMBERS}/${id}`,
                    {
                      from: COMPANY_ROUTES.DETAILS,
                    }
                  )
                }
                display="grid"
                gridTemplateColumns={{
                  _: 'repeat(2, 1fr)',
                  DESKTOP: 'repeat(4, 1fr)',
                }}
              >
                <Datalist.Cell label="id">{id}</Datalist.Cell>
                <Datalist.Cell label="name">{name}</Datalist.Cell>
                <Datalist.Cell label="Email" useEllipsis>
                  {email}
                </Datalist.Cell>
                <Datalist.Cell>
                  <Button
                    isFetching={isRemoveCompanyMemberLoading && rowId === id}
                    disabled={isRemoveCompanyMemberLoading}
                    onClick={(e) => {
                      e.stopPropagation()
                      void handleRemoveCompanyManager({ id, name })
                    }}
                    appearance="ghost"
                    colorVariant="danger"
                    size="small"
                  >
                    {rowId === id && isRemoveCompanyMemberLoading ? (
                      <Spinner size="small" color={COLOR.danger} />
                    ) : (
                      <Icon.Trash />
                    )}
                  </Button>
                </Datalist.Cell>
              </Datalist.Row>
            )}
          />
        </Card>
        {userCount > pageLength && (
          <Pagination.Wrapper>
            <Pagination
              firstPage={1}
              lastPage={Math.ceil((users.data?.users.pagination.total || 1) / pageLength)}
              currentPage={Math.ceil((users.data?.users.pagination.from || 0) / pageLength) + 1}
              onPageChange={setPage}
            />
          </Pagination.Wrapper>
        )}
      </Details>
      <Dialog
        isOpen={!!isDialogOpen}
        onDismiss={() => setIsDialogOpen(false)}
        variant="right"
        maxWidth="450px"
      >
        <Box>
          <Card.Title onDismiss={() => setIsDialogOpen(false)}>Invite manager</Card.Title>
          <Card.Content>
            <FindOrInviteUser
              isLoadingAddMember={isInviteCompanyMemberLoading}
              onUserSelect={handleAddCompanyManager}
            />
          </Card.Content>
        </Box>
      </Dialog>
    </CompanyPageLayout>
  )
}
